import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/api/client';

export type PlatformOverview = {
  totalTenants: number;
  activeTenants: number;
  pendingTenants: number;

  totalBookings: number;
  bookingsToday: number;
  bookingsThisWeek: number;
  bookingsThisMonth: number;

  totalGBV: number;
  gbvThisMonth: number;
  totalPlatformFees: number;
  platformFeesThisMonth: number;

  totalLocations: number;
  totalUnits: number;
  onlineUnits: number;

  tenantsSignedUpThisMonth: number;
  lastBookingAt: string | null;
};

export type PlatformRevenueRow = {
  period: string;
  bookingCount: number;
  grossRevenue: number;
  platformFees: number;
  netToOperators: number;
  activeTenants: number;
};

export type PlatformOperatorLeaderboardRow = {
  tenantId: string;
  name: string;
  slug: string;
  status: 'ACTIVE' | 'PENDING' | 'SUSPENDED';
  totalBookings: number;
  totalRevenue: number;
  platformFeesGenerated: number;
  platformFeePercent: number | null;
  locationCount: number;
  unitCount: number;
  lastBookingAt: string | null;
};

export type PlatformRevenueParams = {
  startDate: string;
  endDate: string;
  groupBy: 'DAY' | 'WEEK' | 'MONTH';
  tenantId?: string;
};

export function usePlatformOverview() {
  return useQuery({
    queryKey: ['platform-overview'],
    queryFn: () => apiClient<PlatformOverview>('/platform/reports/overview'),
    refetchInterval: 60_000,
  });
}

export function usePlatformRevenue(params: PlatformRevenueParams) {
  return useQuery({
    queryKey: ['platform-revenue', params],
    queryFn: () => {
      const sp = new URLSearchParams();
      sp.set('startDate', params.startDate);
      sp.set('endDate', params.endDate);
      sp.set('groupBy', params.groupBy);
      if (params.tenantId) sp.set('tenantId', params.tenantId);
      return apiClient<PlatformRevenueRow[]>(`/platform/reports/revenue?${sp.toString()}`);
    },
  });
}

export function usePlatformLeaderboard(sortBy: string, sortDir: 'asc' | 'desc') {
  return useQuery({
    queryKey: ['platform-leaderboard', sortBy, sortDir],
    queryFn: () =>
      apiClient<PlatformOperatorLeaderboardRow[]>(
        `/platform/reports/operators?sortBy=${sortBy}&sortDir=${sortDir}`,
      ),
  });
}

/**
 * Builds the absolute URL for the CSV export — the consumer passes it
 * to {@code useFileDownload}, which adds the Authorization header and
 * streams the blob as a download. Keeping URL construction here (not
 * inside a mutation hook) means the export button can reuse the same
 * params as the query hook without a separate fetch.
 */
export function buildRevenueCsvUrl(params: PlatformRevenueParams): string {
  const base = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  const sp = new URLSearchParams();
  sp.set('startDate', params.startDate);
  sp.set('endDate', params.endDate);
  sp.set('groupBy', params.groupBy);
  if (params.tenantId) sp.set('tenantId', params.tenantId);
  return `${base}/platform/reports/revenue/export?${sp.toString()}`;
}
